import { useEffect } from "react"

import { useQuery } from "@tanstack/react-query"

import { addDaysToDateString, ukDateFilterRange, ukToday } from "@/constants/sheriffOptions"
import { DateFilter } from "@/types/filters"

import { supabase } from "../lib/supabase"

export interface OverviewStats {
  applications: {
    current: number
    previous: number
    percentChange: number
  }
  uniqueVisitors: {
    current: number
    previous: number
    percentChange: number
  }
  hired: {
    current: number
    previous: number
    percentChange: number
  }
}

/**
 * Process a string date filter and return standardized start and end dates
 * @param dateFilter - The date filter string to process
 * @returns Object containing start and end dates
 */
function processDateFilter(dateFilter: string): { startDate: string; endDate: string } {
  const today = ukToday()
  // "all" and any other filter default to the last 30 days
  return (
    ukDateFilterRange(dateFilter, today) ?? {
      startDate: addDaysToDateString(today, -30),
      endDate: today,
    }
  )
}

/**
 * Hook to fetch overview statistics data
 * @param dateFilter - Optional date filter
 * @returns Query result with overview statistics
 */
export const useOverviewStatsData = (dateFilter?: DateFilter | string, jobId?: string) => {
  // Process the date filter on the frontend
  let processedFilter: { startDate?: string; endDate?: string } = {}

  if (dateFilter) {
    if (typeof dateFilter === "string") {
      // Process string filters (today, this_week, etc.)
      processedFilter = processDateFilter(dateFilter)
    } else {
      // Use the provided date range directly
      processedFilter = dateFilter
    }
  }

  const query = useQuery<OverviewStats>({
    queryKey: ["overviewStats", processedFilter, jobId ?? "all"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("metrics-overview-stats", {
        body: {
          startDate: processedFilter.startDate,
          endDate: processedFilter.endDate,
          jobId: jobId ?? null,
        },
      })

      if (error) {
        throw new Error(`Error fetching overview statistics: ${error.message}`)
      }

      return data
    },
    // Keep data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Enable refetch on window focus
    refetchOnWindowFocus: true,
  })

  // Effect to refetch data when component mounts/tab becomes active
  useEffect(() => {
    // Refetch data when component mounts
    query.refetch()

    // Set up event listener for visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        query.refetch()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Clean up event listener
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [query])

  return query
}
